import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { drw, errText } from "./api";

/** TKO-1/2 — takeoff mode state for the viewer: the session, palette, stamping, selection,
 *  and an undo/redo stack where every entry knows how to reverse itself on the server. */
export function useTakeoff({ projectId, sheetId, setId, reload }) {
  const [session, setSession] = useState(null);
  const [palette, setPalette] = useState(null);
  const [allScopes, setAllScopes] = useState(false);
  const [symbol, setSymbol] = useState(null);
  const [stamping, setStamping] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [finish, setFinish] = useState(false);
  const [projectNet, setProjectNet] = useState(0);
  const undo = useRef([]);
  const redo = useRef([]);
  const [, tick] = useState(0);
  const bump = () => tick((n) => n + 1);

  const refreshSession = useCallback(async (id) => {
    const sid = id || session?.id;
    if (!sid) return;
    const { data } = await drw.get(`/takeoff/sessions/${sid}`);
    setSession(data);
  }, [session?.id]);

  const loadPalette = useCallback(async () => {
    const { data } = await drw.get(`/projects/${projectId}/takeoff/symbols-palette`, { params: { all_scopes: allScopes } });
    setPalette(data);
  }, [projectId, allScopes]);
  useEffect(() => { if (session) loadPalette().catch(() => {}); }, [session, loadPalette]);
  useEffect(() => {
    if (!session) return;
    drw.get(`/projects/${projectId}/takeoff/report`).then(({ data }) => setProjectNet(data.rows.reduce((n, r) => n + (r.app_net || 0), 0))).catch(() => {});
  }, [session, projectId]);

  const enter = async () => {
    try {
      const { data } = await drw.post(`/projects/${projectId}/takeoff/sessions`, { set_id: setId || "" });
      setSession(data);
    } catch (e) { toast.error(errText(e)); }
  };
  const exit = () => { setSession(null); setStamping(false); setSel(new Set()); setSymbol(null); };

  const run = async (entry) => {
    try {
      await entry.do();
      undo.current.push(entry); redo.current = []; bump();
      await reload(true); await refreshSession();
    } catch (e) {
      if (e?.silent) return;
      const d = e?.response?.data?.detail;
      toast.error(d?.code === "installed" ? d.detail : errText(e));
    }
  };
  const doUndo = async () => { const e = undo.current.pop(); if (!e) return; try { await e.undo(); redo.current.push(e); bump(); await reload(true); await refreshSession(); } catch (err) { toast.error(errText(err)); } };
  const doRedo = async () => { const e = redo.current.pop(); if (!e) return; try { await e.do(); undo.current.push(e); bump(); await reload(true); await refreshSession(); } catch (err) { toast.error(errText(err)); } };

  /** Place one unit of `sym` at a sheet point. Undo = void it; redo = restore it. */
  const stamp = (pt, sym = symbol, sourceId = "") => {
    if (!sym || !session) return;
    const ref = { id: null };
    return run({
      do: async () => {
        if (ref.id) { await drw.post(`/takeoff/marks/${ref.id}/restore`); return; }
        const { data } = await drw.post(`/sheets/${sheetId}/takeoff/marks`, { session_id: session.id, symbol_key: sym.key, subject: sym.subject, label: sym.label_full || sym.label, x: pt[0], y: pt[1], source_mark_id: sourceId });
        ref.id = data.id;
      },
      undo: async () => { await drw.post(`/takeoff/marks/${ref.id}/void`, { reason: "undo", session_id: session.id }); },
    });
  };
  const voidMarks = (ids, reason = "") => run({
    do: async () => { for (const i of ids) await drw.post(`/takeoff/marks/${i}/void`, { reason, session_id: session.id }); setSel(new Set()); toast.success(`${ids.length} voided`); },
    undo: async () => { for (const i of ids) await drw.post(`/takeoff/marks/${i}/restore`); },
  });
  // P4-1 — imported Bluebeam marks move too; landing in another room asks "Move to room 104?" first.
  const postMove = async (id, x, y) => {
    try { const { data } = await drw.post(`/takeoff/marks/${id}/move`, { x, y }); toast.success("Moved"); return data; }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code !== "room_change") throw e;
      if (!window.confirm(`${d.detail}\n${d.reason ? `(${d.reason})` : ""}\nThe FRS count moves from room ${d.from_room || "—"} to room ${d.to_room}.`)) throw Object.assign(new Error("cancelled"), { silent: true });
      const { data } = await drw.post(`/takeoff/marks/${id}/move`, { x, y, confirm_room: true });
      toast.success(`Moved to room ${d.to_room}`, { description: `Applied to FRS — room ${d.from_room || "—"} → room ${d.to_room}` });
      return data;
    }
  };
  const move = (m, pt) => {
    const from = m.geometry?.rect ? [(m.geometry.rect[0] + m.geometry.rect[2]) / 2, (m.geometry.rect[1] + m.geometry.rect[3]) / 2] : [pt[0], pt[1]];
    return run({
      do: async () => { await postMove(m.id, pt[0], pt[1]); },
      undo: async () => { await postMove(m.id, from[0], from[1]); },
    });
  };
  const discard = async () => {
    if (!session || !window.confirm("Discard this session? New marks are voided and voided ones restored.")) return;
    try { await drw.post(`/takeoff/sessions/${session.id}/discard`); toast.success("Session discarded"); undo.current = []; redo.current = []; exit(); await reload(true); }
    catch (e) { toast.error(errText(e)); }
  };

  return { session, palette, refreshPalette: loadPalette, allScopes, setAllScopes, symbol, setSymbol, stamping, setStamping, sel, setSel, finish, setFinish,
           projectNet, enter, exit, stamp, voidMarks, move, discard, undo: doUndo, redo: doRedo,
           canUndo: undo.current.length > 0, canRedo: redo.current.length > 0, refreshSession };
}

/** The palette entry a mark belongs to (for Shift+click copies and selecting). */
export const paletteEntryFor = (palette, m) => (palette?.symbols || []).find((s) => s.key === m.symbol_key || s.key === m.symbol?.key)
  || { key: m.symbol_key || m.symbol?.key || "", subject: m.subject, label: m.label, label_full: m.label, shape: m.symbol?.shape, color: m.symbol?.color, mapped: !m.symbol?.unmapped };
